import { useMemo, useState, type CSSProperties } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiPost } from '@lib/apiClient';
import { fmtMemberId } from '@lib/member-id';
import { clientLabel } from '@lib/client-key';
import { useBatch } from '@hooks/useBatch';
import { useIntakeConfig } from '@hooks/useIntakeConfig';
import { ProcessConfirmDialog } from '@components/ProcessConfirmDialog';
import type { ResultCode, SidecarRecord } from '@/types/intake';

import { useCampaign } from '@hooks/useCampaign';
const CYCLE: ResultCode[] = ['Negative', 'Positive', 'Unable to test'];

function nextResult(r: ResultCode): ResultCode {
  return CYCLE[(CYCLE.indexOf(r) + 1) % CYCLE.length]!;
}

/** Solid box color per result: green Negative, red Positive, amber Unable-to-test. */
function boxStyle(r: ResultCode): CSSProperties {
  if (r === 'Positive') return { background: '#B02A37', borderColor: '#92222d', color: '#fff' };
  if (r === 'Unable to test') return { background: '#92450A', borderColor: '#7a3908', color: '#fff' };
  return { background: '#157347', borderColor: '#0f5d39', color: '#fff' };
}

interface LocalResult {
  result: ResultCode;
  reason: string | null;
}

/**
 * The Testing grid: every member moved from the Bench is a box, default green (Negative). Tap to
 * cycle green -> red (Positive) -> amber (Unable to test, type a reason) -> green. Each tap persists
 * (optimistic). "Process all" submits the whole batch into Review.
 */
export function Testing() {
  const { campaign: CAMPAIGN } = useCampaign();
  const qc = useQueryClient();
  const nav = useNavigate();
  const cfg = useIntakeConfig(CAMPAIGN);
  const batch = useBatch(CAMPAIGN);
  const [local, setLocal] = useState<Record<string, LocalResult>>({});
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const items = batch.data?.testing ?? [];
  const view = (r: SidecarRecord): LocalResult => local[r.recordId] ?? { result: r.result, reason: r.reason };

  const counts = useMemo(() => {
    let positive = 0;
    let unable = 0;
    for (const r of items) {
      const v = view(r).result;
      if (v === 'Positive') positive++;
      else if (v === 'Unable to test') unable++;
    }
    return { total: items.length, positive, unable, negative: items.length - positive - unable };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, local]);

  const refresh = () => void qc.invalidateQueries({ queryKey: ['intake-batch', CAMPAIGN] });

  const tap = async (r: SidecarRecord) => {
    const cur = view(r);
    const next = nextResult(cur.result);
    let reason: string | null = null;
    if (next === 'Unable to test') {
      const typed = window.prompt('Reason the sample could not be tested:', cur.reason ?? '');
      if (typed == null) return; // cancelled - keep the current state
      reason = typed.trim() || null;
    }
    const prior = cur; // last-known-good (server value or a prior optimistic write)
    setLocal((m) => ({ ...m, [r.recordId]: { result: next, reason } }));
    setError(null);
    const res = await apiPost('/api/intake/batch/set-result', {
      campaign: CAMPAIGN,
      recordId: r.recordId,
      result: next,
      reason,
    });
    if (!res.ok) {
      // Revert the optimistic write so the box color AND the counts strip reflect what the server
      // actually persisted - otherwise a failed save leaves a wrong result on screen at Process time.
      setLocal((m) => ({ ...m, [r.recordId]: prior }));
      setError((res.data.error as string) || 'Could not save that result');
      refresh();
    }
  };

  const process = async () => {
    setProcessing(true);
    setError(null);
    try {
      const res = await apiPost('/api/intake/batch/transition', { campaign: CAMPAIGN, from: 'testing', to: 'submitted' });
      if (!res.ok) {
        setConfirmOpen(false); // surface the page-level error behind the (now closed) dialog
        setError((res.data.error as string) || 'Could not process the batch');
        refresh();
        return;
      }
      refresh();
      nav('/review');
    } finally {
      setProcessing(false);
    }
  };

  if (cfg.isLoading || !cfg.data) {
    return (
      <div className="li-page">
        <div className="mx-auto max-w-3xl li-state">Loading...</div>
      </div>
    );
  }

  return (
    <div className="li-page">
      <div className="mx-auto max-w-3xl pb-6">
        <div className="li-head flex items-end justify-between gap-3">
          <div>
            <div className="section-eyebrow">Resulting - 26 FOBT</div>
            <h2 className="section-title">Testing</h2>
          </div>
          <button
            type="button"
            onClick={refresh}
            disabled={batch.isFetching}
            className="btn btn-ghost btn-sm disabled:opacity-50"
          >
            {batch.isFetching ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <div className="mb-3 grid grid-cols-4 gap-2">
          {[
            { label: 'Members', value: counts.total },
            { label: 'Negative', value: counts.negative },
            { label: 'Positive', value: counts.positive },
            { label: 'Unable', value: counts.unable },
          ].map((s) => (
            <div key={s.label} className="stat" style={{ padding: '10px 12px' }}>
              <div className="value tab-num" style={{ fontSize: 22 }}>
                {s.value}
              </div>
              <div className="label" style={{ marginBottom: 0, marginTop: 2 }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>

        <p className="mb-3 text-xs" style={{ color: 'var(--ink-400)' }}>
          Everyone starts <span className="font-semibold" style={{ color: '#157347' }}>green (Negative)</span>. Tap a box
          to mark it <span className="font-semibold" style={{ color: '#B02A37' }}>red (Positive)</span>, tap again for{' '}
          <span className="font-semibold" style={{ color: '#92450A' }}>amber (Unable to test)</span> with a reason.
        </p>

        {error && <div className="note danger mb-3">{error}</div>}

        {batch.isLoading && <p className="text-sm" style={{ color: 'var(--ink-400)' }}>Loading batch...</p>}
        {!batch.isLoading && items.length === 0 && (
          <div className="li-state">
            No members in testing. Select members on the Bench and tap "Move all to testing".
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {items.map((r) => {
            const v = view(r);
            return (
              <button
                key={r.recordId}
                type="button"
                onClick={() => void tap(r)}
                className="rounded-cdx border p-3 text-left transition-colors"
                style={boxStyle(v.result)}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-semibold">{r.memberNameSnapshot || '(no name)'}</span>
                  <span
                    className="rounded-cdx px-2 py-0.5 text-xs font-semibold"
                    style={{ background: 'rgba(255,255,255,0.18)' }}
                  >
                    {v.result}
                  </span>
                </div>
                <dl className="mt-1 space-y-0.5 text-xs" style={{ opacity: 0.92 }}>
                  <div>{clientLabel(r.client)} - member {fmtMemberId(r.memberId)}</div>
                  <div>
                    Barcode <span className="font-mono">{r.kitBarcode}</span>
                  </div>
                  <div className="truncate">
                    Tracking <span className="font-mono">{r.returnTracking || '-'}</span>
                  </div>
                  {r.collectionDate && <div>Collected {r.collectionDate}</div>}
                  {v.result === 'Unable to test' && v.reason && <div className="italic">Reason: {v.reason}</div>}
                </dl>
              </button>
            );
          })}
        </div>

        {items.length > 0 && (
          <div
            className="sticky bottom-0 mt-4 py-3"
            style={{ background: 'var(--paper)', borderTop: '1px solid var(--rule)', zIndex: 30 }}
          >
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={processing}
              className="btn-bench w-full disabled:opacity-50"
            >
              {`Process all (${counts.total}) to Review`}
            </button>
          </div>
        )}

        {confirmOpen && (
          <ProcessConfirmDialog
            counts={counts}
            processing={processing}
            onConfirm={() => void process()}
            onCancel={() => setConfirmOpen(false)}
          />
        )}
      </div>
    </div>
  );
}
